import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Stack,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { Add, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  deleteEvent as deleteEventOnDb,
  getEventCalendars,
  saveEvent,
} from "../services/source";

const DEFAULT_CALENDARS = [
  { title: "Trabalho", color: "#0a84ff" },
  { title: "Calendário Fiscal", color: "#34c759" },
];

const WEEK_DAYS = ["S", "T", "Q", "Q", "S", "S", "D"];

const toLocalDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateKey = (dateKey) => {
  if (!dateKey) {
    return null;
  }

  const [year, month, day] = dateKey.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const Event = ({ mode = "full", companyId = null, clientId = null }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactMode = mode === "compact";
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [selectedView, setSelectedView] = useState("month");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [calendarFilter, setCalendarFilter] = useState("all");
  const [calendars, setCalendars] = useState(DEFAULT_CALENDARS);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: "",
    calendarTitle: DEFAULT_CALENDARS[0].title,
    color: DEFAULT_CALENDARS[0].color,
    date: toLocalDateKey(new Date()),
    time: "09:00",
    description: "",
  });

  const resetEventForm = (overrides = {}) => {
    setEventForm({
      title: "",
      calendarTitle: DEFAULT_CALENDARS[0].title,
      color: DEFAULT_CALENDARS[0].color,
      date: toLocalDateKey(new Date()),
      time: "09:00",
      description: "",
      ...overrides,
    });
  };

  const formatDateKey = (date) => toLocalDateKey(date);

  const selectedDayEvents = useMemo(() => {
    const key = formatDateKey(selectedDate);
    return events
      .filter((event) => {
        if (calendarFilter === "all") {
          return event.date === key;
        }

        return event.date === key && event.calendarTitle === calendarFilter;
      })
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [events, selectedDate, calendarFilter]);

  const visibleEvents = useMemo(() => {
    if (calendarFilter === "all") {
      return events;
    }

    return events.filter((event) => event.calendarTitle === calendarFilter);
  }, [events, calendarFilter]);

  const monthName = selectedDate.toLocaleDateString("pt-PT", {
    month: "long",
  });

  const canPersist = Boolean(companyId && clientId);

  const mergeCalendars = useCallback((nextCalendars = []) => {
    if (!nextCalendars.length) {
      setCalendars(DEFAULT_CALENDARS);
      return;
    }

    const merged = [...nextCalendars];

    DEFAULT_CALENDARS.forEach((defaultCalendar) => {
      const exists = merged.some(
        (item) =>
          item.title.toLowerCase() === defaultCalendar.title.toLowerCase(),
      );

      if (!exists) {
        merged.push(defaultCalendar);
      }
    });

    setCalendars(merged);
  }, []);

  const loadEventsData = useCallback(async () => {
    if (!canPersist) {
      return;
    }

    try {
      setEventsLoading(true);
      const response = await getEventCalendars({
        company_id: companyId,
        client_id: clientId,
      });

      setEvents(response?.events || []);
      mergeCalendars(response?.calendars || []);
    } catch (error) {
      console.error("Load events error:", error);
    } finally {
      setEventsLoading(false);
    }
  }, [canPersist, companyId, clientId, mergeCalendars]);

  useEffect(() => {
    if (!isCompactMode) {
      return;
    }

    setSelectedView("month");
  }, [isCompactMode]);

  useEffect(() => {
    loadEventsData();
  }, [loadEventsData]);

  const upsertCalendar = (title, color) => {
    setCalendars((prev) => {
      const exists = prev.some(
        (calendar) => calendar.title.toLowerCase() === title.toLowerCase(),
      );

      if (exists) {
        return prev;
      }

      return [...prev, { title, color }];
    });
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.date) return;

    const normalizedCalendarTitle = eventForm.calendarTitle.trim() || "Geral";
    const normalizedColor = eventForm.color || "#0a84ff";

    const nextEventPayload = {
      id: editingEventId || null,
      title: eventForm.title.trim(),
      calendar_title: normalizedCalendarTitle,
      color: normalizedColor,
      date: eventForm.date,
      time: eventForm.time,
      description: eventForm.description.trim(),
      company_id: companyId,
      client_id: clientId,
    };

    if (canPersist) {
      try {
        const response = await saveEvent(nextEventPayload);

        if (response?.event) {
          setEvents((prev) => {
            const exists = prev.some((event) => event.id === response.event.id);

            if (exists) {
              return prev.map((event) =>
                event.id === response.event.id ? response.event : event,
              );
            }

            return [...prev, response.event];
          });
        }

        if (response?.calendar) {
          upsertCalendar(response.calendar.title, response.calendar.color);
        } else {
          upsertCalendar(normalizedCalendarTitle, normalizedColor);
        }
      } catch (error) {
        console.error("Save event error:", error);
        return;
      }
    } else {
      upsertCalendar(normalizedCalendarTitle, normalizedColor);

      if (editingEventId) {
        setEvents((prev) =>
          prev.map((event) =>
            event.id === editingEventId
              ? {
                  ...event,
                  title: eventForm.title.trim(),
                  calendarTitle: normalizedCalendarTitle,
                  color: normalizedColor,
                  date: eventForm.date,
                  time: eventForm.time,
                  description: eventForm.description.trim(),
                }
              : event,
          ),
        );
      } else {
        setEvents((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: eventForm.title.trim(),
            calendarTitle: normalizedCalendarTitle,
            color: normalizedColor,
            date: eventForm.date,
            time: eventForm.time,
            description: eventForm.description.trim(),
          },
        ]);
      }
    }

    setEditingEventId(null);
    setEventDialogOpen(false);
    resetEventForm({
      calendarTitle: normalizedCalendarTitle,
      color: normalizedColor,
    });
  };

  const handleEditEvent = (event) => {
    setEditingEventId(event.id);
    setEventForm({
      title: event.title || "",
      calendarTitle: event.calendarTitle || DEFAULT_CALENDARS[0].title,
      color: event.color || DEFAULT_CALENDARS[0].color,
      date: event.date || toLocalDateKey(new Date()),
      time: event.time || "09:00",
      description: event.description || "",
    });
    setEventDialogOpen(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (canPersist) {
      try {
        await deleteEventOnDb({
          id: eventId,
          company_id: companyId,
          client_id: clientId,
        });
      } catch (error) {
        console.error("Delete event error:", error);
        return;
      }
    }

    setEvents((prev) => prev.filter((event) => event.id !== eventId));

    if (editingEventId === eventId) {
      setEditingEventId(null);
      setEventDialogOpen(false);
      resetEventForm();
    }
  };

  const getStartOfWeek = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - day);
    return start;
  };

  const shiftPeriod = (direction) => {
    setSelectedDate((prev) => {
      const next = new Date(prev);

      if (selectedView === "year") {
        next.setFullYear(prev.getFullYear() + direction);
      } else if (selectedView === "month") {
        next.setMonth(prev.getMonth() + direction);
      } else if (selectedView === "week") {
        next.setDate(prev.getDate() + direction * 7);
      } else {
        next.setDate(prev.getDate() + direction);
      }

      return next;
    });
  };

  const renderEventCard = (event) => (
    <Paper
      key={event.id}
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderLeft: `4px solid ${event.color || "#0a84ff"}`,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {event.title}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block" }}
      >
        {event.time} · {event.calendarTitle}
      </Typography>
      {event.description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {event.description}
        </Typography>
      ) : null}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={0.5}
        sx={{ mt: 1 }}
      >
        <Button
          size="small"
          startIcon={<EditOutlined fontSize="small" />}
          onClick={() => handleEditEvent(event)}
          sx={{ textTransform: "none", borderRadius: 99 }}
          fullWidth={isSmallScreen}
        >
          Edit
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteOutline fontSize="small" />}
          onClick={() => handleDeleteEvent(event.id)}
          sx={{ textTransform: "none", borderRadius: 99 }}
          fullWidth={isSmallScreen}
        >
          Delete
        </Button>
      </Stack>
    </Paper>
  );

  const renderCalendar = () => {
    if (selectedView === "year") {
      return (
        <Grid container spacing={1}>
          {Array.from({ length: 12 }, (_, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  textAlign: "left",
                  cursor: "pointer",
                  borderRadius: 2,
                  bgcolor:
                    selectedDate.getMonth() === index ? "#eef4ff" : "white",
                }}
                onClick={() =>
                  setSelectedDate(
                    new Date(selectedDate.getFullYear(), index, 1),
                  )
                }
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {new Date(2024, index, 1).toLocaleDateString("pt-PT", {
                    month: "short",
                  })}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "text.secondary" }}
                >
                  {
                    visibleEvents.filter((event) => {
                      const d = parseDateKey(event.date);

                      if (!d) {
                        return false;
                      }

                      return (
                        d.getFullYear() === selectedDate.getFullYear() &&
                        d.getMonth() === index
                      );
                    }).length
                  }{" "}
                  eventos
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (selectedView === "week") {
      const weekStart = getStartOfWeek(selectedDate);
      const weekDays = Array.from({ length: 7 }, (_, offset) => {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + offset);
        return date;
      });

      return (
        <Stack spacing={1.2}>
          {weekDays.map((dayDate) => (
            <Paper
              key={dayDate.toISOString()}
              variant="outlined"
              sx={{
                p: 1.2,
                borderRadius: 2,
                bgcolor:
                  formatDateKey(dayDate) === formatDateKey(selectedDate)
                    ? "#eef4ff"
                    : "white",
                cursor: "pointer",
              }}
              onClick={() => setSelectedDate(dayDate)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {dayDate.toLocaleDateString("pt-PT", {
                  weekday: "short",
                  day: "2-digit",
                })}
              </Typography>
              <Stack spacing={0.8} sx={{ mt: 0.8 }}>
                {visibleEvents
                  .filter((event) => event.date === formatDateKey(dayDate))
                  .slice(0, 2)
                  .map((event) => (
                    <Box
                      key={event.id}
                      sx={{
                        px: 1,
                        py: 0.4,
                        borderRadius: 99,
                        bgcolor: `${event.color || "#0a84ff"}22`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {event.time} {event.title}
                      </Typography>
                    </Box>
                  ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      );
    }

    if (selectedView === "day") {
      return (
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            {selectedDate.toLocaleDateString("pt-PT", {
              weekday: "long",
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </Typography>
          <Box sx={{ mt: 1 }}>
            {selectedDayEvents.length ? (
              selectedDayEvents.map((event) => renderEventCard(event))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum evento para esta data.
              </Typography>
            )}
          </Box>
        </Box>
      );
    }

    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const dayCells = [];

    for (let i = 0; i < firstDay; i += 1) {
      dayCells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      dayCells.push(day);
    }

    while (dayCells.length % 7 !== 0) {
      dayCells.push(null);
    }

    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          {WEEK_DAYS.map((label, index) => (
            <Typography
              key={`day-${index}`}
              variant="caption"
              sx={{
                width: `calc(100% / 7)`,
                textAlign: "center",
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
              }}
            >
              {label}
            </Typography>
          ))}
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            gap: 0.5,
          }}
        >
          {dayCells.map((day, index) => {
            const date = day ? new Date(year, month, day) : null;
            const isSelected =
              day && formatDateKey(date) === formatDateKey(selectedDate);
            const dayEvents = day
              ? visibleEvents.filter(
                  (event) =>
                    event.date === formatDateKey(new Date(year, month, day)),
                )
              : [];

            return (
              <Box key={index}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 0.5, sm: 0.75 },
                    minHeight: { xs: 54, sm: 62 },
                    borderRadius: 2,
                    bgcolor: isSelected ? "#eef4ff" : "white",
                    borderColor: day ? "divider" : "transparent",
                    cursor: day ? "pointer" : "default",
                  }}
                  onClick={() => day && setSelectedDate(date)}
                >
                  {day ? (
                    <>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: { xs: "0.68rem", sm: "0.75rem" } }}
                      >
                        {day}
                      </Typography>
                      {!isSmallScreen && dayEvents.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: "text.secondary" }}
                        >
                          {dayEvents.length} evt
                        </Typography>
                      )}
                    </>
                  ) : null}
                </Paper>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Grid item xs={12} lg={isCompactMode ? 4 : 12}>
      <Paper
        sx={{
          p: { xs: 1.25, sm: 2 },
          borderRadius: 4,
          border: "1px solid #e6e6ea",
          background:
            "linear-gradient(180deg, rgba(250,250,252,0.95) 0%, rgba(245,246,249,0.95) 100%)",
        }}
      >
        <Grid container spacing={2}>
          {!isCompactMode ? (
            <Grid item xs={12} md={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  borderColor: "#e1e3e8",
                  backgroundColor: "#f8f9fc",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Calendário
                </Typography>
                <Stack spacing={1}>
                  <Button
                    variant={calendarFilter === "all" ? "contained" : "text"}
                    size="small"
                    onClick={() => setCalendarFilter("all")}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                  >
                    Todos
                  </Button>
                  {calendars.map((calendar) => {
                    const count = events.filter(
                      (event) => event.calendarTitle === calendar.title,
                    ).length;

                    return (
                      <Button
                        key={calendar.title}
                        variant={
                          calendarFilter === calendar.title
                            ? "contained"
                            : "text"
                        }
                        size="small"
                        onClick={() => setCalendarFilter(calendar.title)}
                        sx={{
                          justifyContent: "space-between",
                          textTransform: "none",
                          borderRadius: 2,
                          color:
                            calendarFilter === calendar.title
                              ? "white"
                              : "text.primary",
                          backgroundColor:
                            calendarFilter === calendar.title
                              ? calendar.color
                              : "transparent",
                          "&:hover": {
                            backgroundColor:
                              calendarFilter === calendar.title
                                ? calendar.color
                                : `${calendar.color}15`,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor:
                                calendarFilter === calendar.title
                                  ? "white"
                                  : calendar.color,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              whiteSpace: "normal",
                              lineHeight: 1.15,
                              textAlign: "left",
                              maxWidth: { md: 110, lg: 96 },
                            }}
                          >
                            {calendar.title}
                          </Typography>
                        </Box>
                        <Chip size="small" label={count} />
                      </Button>
                    );
                  })}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" color="text.secondary">
                  Hoje
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {new Date().toLocaleDateString("pt-PT", {
                    day: "2-digit",
                    month: "short",
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedDayEvents.length} evento(s) na data selecionada
                </Typography>
              </Paper>
            </Grid>
          ) : null}

          <Grid item xs={12} md={isCompactMode ? 12 : 9}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1, sm: 1.5 },
                borderRadius: 3,
                borderColor: "#e1e3e8",
                backgroundColor: "white",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Calendário de eventos
                </Typography>
                {!isCompactMode ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => {
                      setEditingEventId(null);
                      resetEventForm();
                      setEventDialogOpen(true);
                    }}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Novo evento
                  </Button>
                ) : null}
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
              >
                {!isCompactMode ? (
                  <ToggleButtonGroup
                    value={selectedView}
                    exclusive
                    size="small"
                    onChange={(_, value) => value && setSelectedView(value)}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      flexWrap: "wrap",
                      "& .MuiToggleButton-root": {
                        px: { xs: 1.1, sm: 1.5 },
                        py: { xs: 0.45, sm: 0.6 },
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      },
                    }}
                  >
                    <ToggleButton value="year">ANO</ToggleButton>
                    <ToggleButton value="month">MÊS</ToggleButton>
                    <ToggleButton value="week">SEMANA</ToggleButton>
                    <ToggleButton value="day">DIA</ToggleButton>
                  </ToggleButtonGroup>
                ) : (
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    MÊS
                  </Typography>
                )}

                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  justifyContent={{ xs: "center", sm: "flex-start" }}
                >
                  <IconButton size="small" onClick={() => shiftPeriod(-1)}>
                    <Typography variant="caption">◀</Typography>
                  </IconButton>
                  <Button
                    size="small"
                    onClick={() => setSelectedDate(new Date())}
                    sx={{ textTransform: "none", borderRadius: 99 }}
                  >
                    HOJE
                  </Button>
                  <IconButton size="small" onClick={() => shiftPeriod(1)}>
                    <Typography variant="caption">▶</Typography>
                  </IconButton>
                </Stack>
              </Stack>

              <Typography
                variant="subtitle2"
                sx={{
                  textAlign: "center",
                  mb: 1.5,
                  textTransform: "capitalize",
                  fontWeight: 700,
                }}
              >
                {selectedView === "year"
                  ? selectedDate.getFullYear()
                  : `${monthName} ${selectedDate.getFullYear()}`}
              </Typography>

              {renderCalendar()}

              {selectedView !== "day" ? (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 800 }}
                  >
                    Eventos da data selecionada
                  </Typography>
                  <Stack spacing={1}>
                    {eventsLoading ? (
                      <Typography variant="body2" color="text.secondary">
                        A carregar eventos...
                      </Typography>
                    ) : selectedDayEvents.length ? (
                      selectedDayEvents.map((event) => renderEventCard(event))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Nenhum evento para esta data.
                      </Typography>
                    )}
                  </Stack>
                </>
              ) : null}
            </Paper>
          </Grid>
        </Grid>
      </Paper>
      <Dialog
        open={eventDialogOpen}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: { xs: "calc(100% - 24px)", sm: "88vw", md: "820px" },
            maxWidth: "820px",
          },
        }}
        onClose={() => {
          setEventDialogOpen(false);
          setEditingEventId(null);
          resetEventForm();
        }}
      >
        <DialogTitle>
          {editingEventId ? "Editar evento" : "Novo evento"}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <Divider />
          <TextField
            label="Titulo do evento"
            fullWidth
            value={eventForm.title}
            onChange={(e) =>
              setEventForm({ ...eventForm, title: e.target.value })
            }
          />
          <TextField
            label="Titulo do calendario"
            fullWidth
            value={eventForm.calendarTitle}
            onChange={(e) =>
              setEventForm({ ...eventForm, calendarTitle: e.target.value })
            }
          />
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 1.2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: { xs: "auto", sm: 46 } }}
            >
              Cor
            </Typography>
            <TextField
              type="color"
              value={eventForm.color}
              onChange={(e) =>
                setEventForm({ ...eventForm, color: e.target.value })
              }
              sx={{ width: { xs: 120, sm: 90 } }}
            />
            <FormControl fullWidth>
              <InputLabel id="calendar-template-label">Calendário</InputLabel>
              <Select
                labelId="calendar-template-label"
                value={
                  calendars.some(
                    (item) => item.title === eventForm.calendarTitle,
                  )
                    ? eventForm.calendarTitle
                    : ""
                }
                label="Calendário"
                onChange={(e) => {
                  const calendar = calendars.find(
                    (item) => item.title === e.target.value,
                  );

                  if (!calendar) {
                    return;
                  }

                  setEventForm({
                    ...eventForm,
                    calendarTitle: calendar.title,
                    color: calendar.color,
                  });
                }}
              >
                <MenuItem value="">
                  <em>Escolher calendario existente</em>
                </MenuItem>
                {calendars.map((calendar) => (
                  <MenuItem key={calendar.title} value={calendar.title}>
                    {calendar.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TextField
            label="Data"
            type="date"
            fullWidth
            value={eventForm.date}
            onChange={(e) =>
              setEventForm({ ...eventForm, date: e.target.value })
            }
          />
          <TextField
            label="Hora"
            type="time"
            fullWidth
            value={eventForm.time}
            onChange={(e) =>
              setEventForm({ ...eventForm, time: e.target.value })
            }
          />
          <TextField
            label="Descricao do evento"
            multiline
            minRows={3}
            fullWidth
            value={eventForm.description}
            onChange={(e) =>
              setEventForm({ ...eventForm, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions
          sx={{ flexDirection: { xs: "column", sm: "row" }, gap: 1 }}
        >
          <Button
            onClick={() => {
              setEventDialogOpen(false);
              setEditingEventId(null);
              resetEventForm();
            }}
            fullWidth={isSmallScreen}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveEvent}
            fullWidth={isSmallScreen}
          >
            {editingEventId ? "Atualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default Event;
